import * as Sentry from "@sentry/react";
import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { subscribeWithSelector } from "zustand/middleware";
import type { Session } from "@supabase/supabase-js";
import {
	type Subscription,
	checkForActiveSubscription,
} from "@coffitivity/shared";
import { fetchCurrentUserPlan } from "../../infrastructure/CurrentUserPlanService";
export type { Subscription };

declare global {
	interface Window {
		__APP_SESSION__?: AppStoreProps["subscription"];
	}
}

export type ModalIds =
	| "ACCOUNT_REQUIRED_MODAL"
	| "EXPIRED_LISTENING_MODAL"
	| "SIGNUP_MODAL"
	| "UPGRADE_MODAL"
	| null;

type AppState = {
	isAuthLoading: boolean;
	isUserSubscribed: boolean;
	isUserPremium: boolean;
	isUserEngaged: boolean;
	openedModalId: ModalIds;
	session: Session | null;
	subscription: Subscription | null;
};

type AppActions = {
	setAuthLoading: (isLoading: boolean) => void;
	setSession: (session: Session | null) => Promise<void>;
	setSubscription: (subscription: Subscription | null) => void;
	setUserEngagementStatus: (isEngaged: boolean) => void;
	showModal: (id: ModalIds, isVisible: boolean) => void;
};

export type AppStoreProps = AppState & AppActions;

export const appStore = createStore<AppStoreProps>()(
	subscribeWithSelector(
		immer((set) => ({
			isAuthLoading: false,
			isUserSubscribed: false,
			isUserEngaged: false,
			isUserPremium: false,
			openedModalId: null,
			session: null,
			subscription: null,
			setAuthLoading: (isLoading: boolean) =>
				set((state) => {
					state.isAuthLoading = isLoading;
				}),
			setSession: async (session: Session | null) => {
				if (!session) {
					set((state) => {
						state.session = null;
						state.isUserSubscribed = false;
						state.isUserPremium = false;
						state.subscription = null;
					});
				} else if (session.user) {
					try {
						const { subscription } = await fetchCurrentUserPlan(session);

						set((state) => {
							state.session = session;
							state.subscription = subscription;
							state.isUserSubscribed = checkForActiveSubscription(
								subscription,
							);
							state.isUserPremium =
								subscription?.planTier === "PREMIUM" ||
								subscription?.id === "ONE_TIME_PURCHASE";
						});
					} catch (error) {
						Sentry.captureException(error);
						set((state) => {
							state.session = session;
							state.isUserSubscribed = false;
							state.isUserPremium = false;
							state.subscription = null;
						});
					}
				}

				// set the session on the window so that we can access it from the console for debugging purposes
				window.__APP_SESSION__ = appStore.getState().subscription;
			},
			setSubscription: (subscription: Subscription | null) =>
				set((state) => {
					state.subscription = subscription;
					state.isUserSubscribed = checkForActiveSubscription(subscription);
					state.isUserPremium =
						subscription?.planTier === "PREMIUM" ||
						subscription?.id === "ONE_TIME_PURCHASE";
				}),
			setUserEngagementStatus: (isEngaged: boolean) =>
				set((state) => {
					state.isUserEngaged = isEngaged;
				}),
			showModal: (id: ModalIds, isVisible: boolean) =>
				set((state) => {
					if (isVisible) {
						state.openedModalId = id;
					} else {
						state.openedModalId = null;
					}
				}),
		})),
	),
);

export type AppStore = typeof appStore;

export const useAppStore = () => {
	const store = useStore(appStore);
	return store;
};
